import { Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import { FieldHeader } from '../common/FieldHeader';
import { NumberDisplay } from './NumberDisplay';
import { NumberEmptyState } from './NumberEmptyState';
import { NumberUpdateModal } from './NumberUpdateModal';
import { NumberDiffModal } from './NumberDiffModal';
import { FormFieldNumberInput } from '@publishing-engine/domain/types/article';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

export type IFormFieldNumber = FormFieldNumberInput;

type IProps = {
  formFieldDefinition: IFormFieldNumber;
  value: number;
  previousValue?: number | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: number) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?:boolean;
};

export const GenericNumberField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <NumberUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: number) => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <NumberDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {typeof value === 'number' ? (
        <NumberDisplay value={value} />
      ) : (
        <NumberEmptyState />
      )}
    </Flex>
  );
};
